import React, { useState, useEffect } from "react";
import { RCCButton, RCCColorPicker, RCCGrid, RCCHookToast, RCCInput, RCCServicesApi, RCCTextArea, RCCTooltip } from "react-common-components";
import { useForm, useWatch } from "react-hook-form";
import FadedDiv from "src/components/Animations/fade";
import LovIndicators from "src/components/Lov/Indicators";
import LovMaps from "src/components/Lov/Maps";
import LovOKR from "src/components/Lov/OKR";
import LovProgram from "src/components/Lov/Program";
import LovProject from "src/components/Lov/Projects";
import { colorPickerStyle } from "src/components/ModalIndicator/styles";
import Title from "src/components/Title";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import returnColor from "src/utils/returnColor";
import stringToNumber from "src/utils/stringToNumber";
import dashboardTemplates from "../dashboardTemplates";
import { FormatDashboard, IEditableCards, TypeDashboardComponents } from "../interface";
import { IEditDashboardComponent } from "./interface";
import Container from "./styles";

const EditDashboardComponent: React.FC<IEditDashboardComponent> = ({
	card,
	callbackChangeCard,
	deleteCard
}) => {
	const { translate } = useTranslation();
	const {confirm} = useConfirmModal();
	const { handleSubmit, register, control, setValue } = useForm();
	const { addToast } = RCCHookToast();

	const [isEditingAField, setIsEditingAField] = useState(false);
	const [internalCard, setInternalCard] = useState(card);

	const fieldColor = useWatch({
		control,
		name: "daa002Cor",
		defaultValue: card.daa002Cor
	});

	const onChangeLov = (e: any) => {
		const value = e !== null ? e.value : null;
		verifyChanges({daa002Idorigem: value !== null ? Number(value) : null});
	};

	const verifyChanges = (cardChange: IEditableCards) => {
		setInternalCard((prevState) => ({
			...prevState,
			...cardChange
		}));
	};

	const lovs = (paramFormat: FormatDashboard, paramType: TypeDashboardComponents) => {
		const idOrigin = card.daa002Idorigem ? card.daa002Idorigem.toString() : "";
		switch(paramFormat){
		case FormatDashboard.PROJECT:
			if(paramType === TypeDashboardComponents.CARD)
				return (
					<LovProject
						control={control}
						name="daa002Idorigem"
						label={translate("Projeto")}
						defaultValue={idOrigin}
						isClearable
						onChangeCapture={onChangeLov}
					/> 
				);
			if(paramType === TypeDashboardComponents.PIE || paramType === TypeDashboardComponents.CARD_3)
				return (
					<LovProgram
						control={control}
						name="daa002Idorigem"
						label={translate("Programa")}
						defaultValue={idOrigin}
						isClearable
						onChangeCapture={onChangeLov}
					/> 
				);
			return (<></>);
		case FormatDashboard.INDICATORS:
			if(paramType === TypeDashboardComponents.GAUGE)
				return (
					<LovIndicators
						control={control}
						name="daa002Idorigem"
						label={translate("Indicador")}
						defaultValue={idOrigin}
						isClearable
						onChangeCapture={onChangeLov}
					/> 
				);
			return (<></>);
		case FormatDashboard.STRATEGIC_PLANNING:
			if(paramType === TypeDashboardComponents.CARD_3)
				return (
					<LovMaps
						control={control}
						name="daa002Idorigem"
						label={translate("Mapas")}
						defaultValue={idOrigin}
						isClearable
						onChangeCapture={onChangeLov}
					/> 
				);
			if(paramType === TypeDashboardComponents.CARD || paramType === TypeDashboardComponents.CARD_2)
				return (
					<LovOKR
						control={control}
						name="daa002Idorigem"
						label="OKRs"
						defaultValue={idOrigin}
						isClearable
						onChangeCapture={onChangeLov}
					/> 
				);	
			return(<></>);
		default: 
			return(<></>);
		}
	};

	const executeDeleteCard = () => {
		confirm(
			translate("Exclusão de card"), 
			`${translate("Deseja realmente excluir o card")}?`,
			async () => {
				await deleteCard(card.daa002Id);
				addToast({
					color: "green",
					description: translate("Card removido com sucesso"),
					title: translate("Sucesso")
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao remover card no dashboard"), addToast);
			},
			true
		);
	};

	const onSubmit = async (data: any) => {
		try{
			const dataForm = data;

			let body = {
				daa002Id: card.daa002Id,
				...dataForm
			};
			
			if("daa002Idorigem" in data)
				body = {
					...body,
					daa002Idorigem: typeof dataForm.daa002Idorigem === "string" ? stringToNumber(dataForm.daa002Idorigem) : dataForm.daa002Idorigem
				};	
			
			const response = await RCCServicesApi.putRequest({
				method: "Dashboard/EditCard",
				body: {
					...body
				}
			});

			addToast({
				color: "green",
				description: translate("Card alterado com sucesso"),
				title: translate("Sucesso")
			});

			setIsEditingAField(false);

			callbackChangeCard(card.daa002Id, response.data);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao alterar card no dashboard"), addToast);
		}
	};

	useEffect(() => {
		if(internalCard.daa002Titulo === card.daa002Titulo && 
		   internalCard.daa002Descricao === card.daa002Descricao && 
		   internalCard.daa002Cor === card.daa002Cor &&
		   internalCard.daa002Idorigem === card.daa002Idorigem){
			setIsEditingAField(false);
			return;
		}
		setIsEditingAField(true);
	}, [internalCard]);

	return <Container onSubmit={handleSubmit(onSubmit)}>
		<RCCGrid container item justifyContent="space-between">
			<Title color="purple" size="sm">
				{translate(dashboardTemplates.find((x) => x.type === card.daa002Tipo && x.format === card.daa002Formato)?.title || "")}
			</Title>
			<div style={{ display: "flex" }}>
				<RCCTooltip
					text={translate("Excluir")}
					color="red"	
				>
					<RCCButton 
						onClick={executeDeleteCard} 
						size="xs" 
						color="transparent" 
						icon={{
							name:"trash",
							size: 14,
							color: "var(--red)"
						}} 
						layout="isRounded"
					/>
				</RCCTooltip>
				<RCCColorPicker
					name="daa002Cor"
					control={control}
					label=""
					labelPosition="start"
					colorPickerProps ={{
						color: returnColor(fieldColor || "#ffffff0")
					}}
					defaultValue = { returnColor(fieldColor || "") }
					position="leftEnd"
					onColorChange={(color) => verifyChanges({daa002Cor: color})}
					styleInline={colorPickerStyle}
				/>

				{fieldColor && (
					<RCCTooltip
						text={translate("Limpar cor")}
						color="yellow"
					>
						<RCCButton
							onClick={() => {
								verifyChanges({daa002Cor: ""});
								setValue("daa002Cor","");
							}} 
							size="xs" 
							color="transparent" 
							icon={{
								name:"broom",
								size:14,
								color: "var(--yellow)"
							}} 
							layout="isRounded"
						/>
					</RCCTooltip>
				)}
			</div>
		</RCCGrid>
		<RCCInput 
			register={register}
			label={translate("Título")}
			name="daa002Titulo"
			defaultValue={card.daa002Titulo.trim()}
			maxLength={100}
			onBlurCapture={(e) => verifyChanges({daa002Titulo: e.target.value})}
		/>
		<RCCTextArea 
			register={register}
			label={translate("Descrição")}
			name="daa002Descricao"
			defaultValue={card.daa002Descricao.trim()}
			maxLength={4000}
			onBlurCapture={(e) => verifyChanges({daa002Descricao: e.target.value})}
		/>
		{lovs(card.daa002Formato as FormatDashboard, card.daa002Tipo)}
		{isEditingAField && (
			<FadedDiv>
				<RCCButton 
					color="green"
					icon={{
						name: "checkCircle",
						color: "#FFF"
					}}
					type="submit"
				>
					{translate("Confirmar alteração")}
				</RCCButton>
			</FadedDiv>
		)}
	</Container>;
};

export default EditDashboardComponent;
